import { FC, useContext, useEffect } from 'react'
import { AddNewExternalUserProps, CreateNewAdversary, ErrorException } from '../../interfaceTypes'
import { AppContext, CaseContext } from '../../context'
import { Controller, FormProvider, SubmitHandler, useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import { addNewExternalUserValidationSchema } from '../../validationSchema'
import { ExternalUserStatus, useCreateExternalUserMutation } from '../../../generated'
import { Alert } from '../common/Alert'
import { ActionType } from '../../context/AppContextReducer'
import { titleize } from '../../utils'
import { CustomModal } from '../common/CustomModal'
import { InputFormFields } from './InputFormFields'
import { Button, CircularProgress, DialogActions, FormControl, Grid, InputLabel, MenuItem, Select } from '@mui/material'
import { CustomController } from '../common/CustomController'
import { CANCEL, US_STATES } from '../../constants'

const AddContact: FC<AddNewExternalUserProps> = ({ refetch, setNewExternalUser, customOpen, handleCustomClose }) => {
  const { contactRoleId } = useContext(CaseContext)
  const { isOpen: isNewContactOpen, dispatch: appDispatch } = useContext(AppContext)
  const defaultState = US_STATES.filter(state => state.name === 'New Jersey')

  const methods = useForm<CreateNewAdversary>({
    mode: 'all',
    resolver: yupResolver(addNewExternalUserValidationSchema),

    defaultValues: {
      firstName: '',
      lastName: '',
      businessName: '',
      status: ExternalUserStatus.Active,
      externalUserRoleId: contactRoleId,
      phoneNumber: '',
      email: '',
      addresses: [
        {
          address1: '',
          address2: '',
          country: 'USA',
          county: '',
          externalUserId: '',
          state: defaultState[0].name
        }
      ]
    }
  })

  const [createExternalUserMutation, { loading: creteExternalUserLoader }] = useCreateExternalUserMutation({
    onError({ graphQLErrors }) {
      if (graphQLErrors) {
        graphQLErrors.map(({ extensions }) => {
          if (extensions) {
            const { exception } = extensions
            if (exception) {
              const { response } = exception as ErrorException
              const { response: errorResponse } = response || {}
              if (errorResponse) {
                const { error } = errorResponse
                return Alert.error(error)
              }
            }
          }
          return null
        })
      }
    },

    onCompleted(data) {
      if (data) {
        const {
          createExternalUser: { response, externalUser }
        } = data || {}

        if (response) {
          const { status } = response

          if (status === 200 && externalUser) {
            Alert.success('Contact Created Successfully')
            setNewExternalUser && setNewExternalUser(externalUser)

            if (isNewContactOpen || customOpen) handleClose()
          }
        }
        refetch?.()
      }
    }
  })

  const { handleSubmit, control, reset, setValue } = methods

  const handleClose = () => {
    if (handleCustomClose) handleCustomClose()
    else appDispatch({ type: ActionType.SET_IS_OPEN, isOpen: false })
    reset()
  }

  const onSubmit: SubmitHandler<CreateNewAdversary> = async (data: CreateNewAdversary) => {
    const { firstName, lastName, phoneNumber, homePhone, personalPhone, dateOfBirth, ...rest } = data

    await createExternalUserMutation({
      variables: {
        createExternalUserInput: {
          ...rest,
          dateOfBirth: !!dateOfBirth ? dateOfBirth : undefined,
          firstName: firstName?.trim() || undefined,
          lastName: lastName?.trim() || undefined,
          externalUserRoleId: contactRoleId,
          phoneNumber: phoneNumber ?? undefined,
          homePhone: homePhone ?? undefined,
          personalPhone: personalPhone ?? undefined
        }
      }
    })
  }

  useEffect(() => {
    if (contactRoleId) setValue('externalUserRoleId', contactRoleId)
    // eslint-disable-next-line
  }, [contactRoleId, isNewContactOpen])

  return (
    <CustomModal title={'Add New Contact'} isOpen={customOpen ?? !!isNewContactOpen} handleClose={handleClose}>
      <FormProvider {...methods}>
        <form
          onSubmit={event => {
            event.stopPropagation()
            return handleSubmit(onSubmit)(event)
          }}
        >
          <Grid container spacing={2} rowSpacing={2}>
            <InputFormFields isClient />

            <Grid item md={12} sm={12} xs={12}>
              <CustomController
                controllerName="addresses[0].address1"
                controllerLabel="Address line 1"
                fieldType="text"
              />
            </Grid>

            <Grid item md={12} sm={12} xs={12}>
              <CustomController
                controllerName="addresses[0].address2"
                controllerLabel="Address line 2"
                fieldType="text"
              />
            </Grid>

            <Grid item md={4} sm={12} xs={12}>
              <CustomController controllerName="addresses[0].city" controllerLabel="City" fieldType="text" />
            </Grid>

            <Grid item md={4} sm={12} xs={12}>
              <FormControl variant="outlined" fullWidth sx={{ marginTop: 1 }}>
                <InputLabel id="address-state">State</InputLabel>

                <Controller
                  name="addresses.0.state"
                  control={control}
                  render={({ field }) => (
                    <Select
                      {...field}
                      labelId="address-state"
                      label="State"
                      fullWidth
                      MenuProps={{ style: { maxHeight: 300 } }}
                    >
                      {US_STATES.map((item, index) => {
                        const { name } = item || {}
                        return (
                          <MenuItem value={name} key={`${item}-${index}`}>
                            {titleize(name)}
                          </MenuItem>
                        )
                      })}
                    </Select>
                  )}
                />
              </FormControl>
            </Grid>

            <Grid item md={2} sm={12} xs={12}>
              <CustomController
                controllerName="addresses[0].country"
                controllerLabel="Country"
                fieldType="text"
                readOnly
              />
            </Grid>

            <Grid item md={2} sm={12} xs={12}>
              <CustomController controllerName="addresses.0.zipCode" controllerLabel="Zip Code" fieldType="text" />
            </Grid>
          </Grid>

          <DialogActions sx={{ padding: '30px 0 0' }}>
            <Button onClick={handleClose} variant="outlined" aria-label={CANCEL}>
              {CANCEL}
            </Button>

            <Button
              type="submit"
              color="primary"
              variant="contained"
              disabled={creteExternalUserLoader}
              aria-label="add new adversary"
              endIcon={creteExternalUserLoader && <CircularProgress size={20} color="inherit" />}
            >
              Add
            </Button>
          </DialogActions>
        </form>
      </FormProvider>
    </CustomModal>
  )
}

export default AddContact
